import React, { useMemo } from 'react'
import { generateStudPositions, BRICK_CONSTANTS } from '@utils/brickGeometry'
import { isDebugMode } from '@utils/debug'
import type { BrickType, BrickColor } from '@models/brick'

interface BrickStudsProps {
  type: BrickType
  rotation: number
  color: BrickColor
  highlighted?: boolean // Show green highlight for valid connection (only in debug mode)
}

/**
 * BrickStuds Component
 *
 * Renders the cylindrical studs on top of a brick.
 * Studs are the connection points that allow bricks to stack vertically.
 *
 * Features:
 * - Cylindrical geometry matching LEGO stud proportions
 * - Positioned relative to parent brick (use as child of mesh)
 * - Slightly glossier material than brick body
 * - Optional green highlight for valid connection points
 * - Optimized: geometry generated once per render
 *
 * Visual Design:
 * - Diameter: 0.4 units (half the grid spacing)
 * - Height: 0.2 units (protruding from brick surface)
 * - Position: Centered on each grid unit on top surface
 * - Material: Same color as brick but slightly glossier
 *
 * @param type - The brick type (determines stud count/layout)
 * @param rotation - Rotation in degrees (affects stud pattern for non-square bricks)
 * @param color - RGB color for the studs
 * @param highlighted - If true, shows green emissive glow for valid connections
 */
export const BrickStuds: React.FC<BrickStudsProps> = ({
  type,
  rotation,
  color,
  highlighted = false,
}) => {
  // Calculate stud positions relative to brick center
  const studPositions = useMemo(
    () => generateStudPositions(type, rotation),
    [type, rotation]
  )

  const { STUD_DIAMETER, STUD_HEIGHT } = BRICK_CONSTANTS

  // Check if debug mode is enabled
  const debugMode = isDebugMode()

  // Color for studs (same as brick, or green when highlighted in debug mode)
  const studColor = (highlighted && debugMode)
    ? 'rgb(50, 255, 50)' // Bright green for valid connection (debug mode only)
    : `rgb(${color.r}, ${color.g}, ${color.b})`

  return (
    <group>
      {studPositions.map((position, index) => (
        <mesh
          key={`stud-${index}`}
          position={[position.x, position.y, position.z]}
          castShadow
        >
          {/* Cylinder: radius, height, radial segments */}
          <cylinderGeometry
            args={[STUD_DIAMETER / 2, STUD_DIAMETER / 2, STUD_HEIGHT, 16]}
          />
          <meshStandardMaterial
            color={studColor}
            roughness={0.2} // Glossier than brick body
            metalness={0.1}
            emissive={(highlighted && debugMode) ? 'rgb(0, 255, 0)' : undefined}
            emissiveIntensity={(highlighted && debugMode) ? 0.5 : 0}
          />
        </mesh>
      ))}
    </group>
  )
}
